import { useBedrockRegistration } from 'store/bedrockRegistration';
import { useFeatureFlagContext } from 'lib/ContextTypes';
import { useVertical } from 'lib/Hooks/useVertical';
import { getFeatureConfigForBrand } from 'lib/getFeatureStatus';
import { HIDE_TABOOLA_ADS } from 'lib/brandFeatures';
import { testSubscription } from 'lib/cookies';

/**
 * Hook to check if ads should be hidden for the current user.
 * Checks all of:
 * - Feature flag 'nbc-news-subscriptions' is enabled OR testSubscription cookie is set
 * - User has nbcNews subscription (ad-free) OR testSubscription cookie is set
 * - User is on NBC News site (vertical === 'news')
 *
 * @returns {boolean} True if ads should be hidden, false otherwise
 */
export function useShouldRemoveAds() {
  const featureFlags = useFeatureFlagContext();
  const subscriptions = useBedrockRegistration((state) => state.subscriptions);
  const { vertical } = useVertical();

  return Boolean(
    (featureFlags?.['nbc-news-subscriptions'] || testSubscription)
    && (subscriptions?.nbcNews || testSubscription)
    && vertical === 'news',
  );
}

/**
 * Hook to check if Taboola ads should be removed for the current user.
 * Checks all of:
 * - Brand-level feature config (HIDE_TABOOLA_ADS) is enabled for this brand
 * - Feature flag 'nbc-news-subscriptions' is enabled OR testSubscription cookie is set
 * - Override flag 'show-taboola-for-subscribers' is NOT set
 * - User has nbcNews subscription (ad-free) OR testSubscription cookie is set
 * - User is on NBC News site (vertical === 'news')
 *
 * @returns {boolean} True if Taboola ads should be hidden, false otherwise.
 */
export function useShouldRemoveTaboolaAds() {
  const featureFlags = useFeatureFlagContext();
  const { vertical } = useVertical();
  const subscriptions = useBedrockRegistration((state) => state.subscriptions);

  return Boolean(
    getFeatureConfigForBrand(HIDE_TABOOLA_ADS, vertical)
    && (featureFlags?.['nbc-news-subscriptions'] || testSubscription)
    && !featureFlags?.['show-taboola-for-subscribers']
    && (subscriptions?.nbcNews || testSubscription)
    && vertical === 'news',
  );
}
